import React, { useEffect, useState } from 'react';
import { BarChart3, Download, Eye, MousePointerClick, Phone, Navigation, Globe, Loader2 } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader } from './AdminUI';

const PERIODS = [
  { days: 7, label: '7 giorni' },
  { days: 30, label: '30 giorni' },
  { days: 90, label: '90 giorni' },
  { days: 365, label: '1 anno' }
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const load = () => {
    setLoading(true);
    adminApi.analyticsSummary(days).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [days]);

  const rows = (data?.rows || []).filter(r =>
    !q || r.name?.toLowerCase().includes(q.toLowerCase()) || r.city_slug?.includes(q)
  );

  const totals = (rows).reduce((acc, r) => ({
    views: acc.views + r.views,
    clicks: acc.clicks + r.clicks,
    calls: acc.calls + r.calls,
    directions: acc.directions + r.directions,
    websites: acc.websites + r.websites
  }), { views: 0, clicks: 0, calls: 0, directions: 0, websites: 0 });

  const csvUrl = adminApi.analyticsCsvUrl(days);
  const token = typeof window !== 'undefined' ? localStorage.getItem('yup_admin_token') : '';

  const downloadCsv = async () => {
    const resp = await fetch(csvUrl, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `yup-analytics-${days}d.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Statistiche sponsor"
        subtitle="Visualizzazioni e click per attività — utile da mostrare agli inserzionisti"
        action={
          <div className="flex items-center gap-2">
            <select value={days} onChange={e => setDays(Number(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="analytics-period">
              {PERIODS.map(p => <option key={p.days} value={p.days}>{p.label}</option>)}
            </select>
            <button onClick={downloadCsv} className="btn-primary flex items-center gap-2" data-testid="analytics-csv-btn">
              <Download size={16}/> CSV
            </button>
          </div>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Kpi icon={Eye} label="Views" value={totals.views} color="blue"/>
        <Kpi icon={MousePointerClick} label="Details" value={totals.clicks} color="indigo"/>
        <Kpi icon={Phone} label="Call" value={totals.calls} color="green"/>
        <Kpi icon={Navigation} label="Directions" value={totals.directions} color="amber"/>
        <Kpi icon={Globe} label="Website" value={totals.websites} color="purple"/>
      </div>

      <div className="mb-3 flex items-center gap-3">
        <input type="text" placeholder="Filtra per nome attività / città..." value={q} onChange={e => setQ(e.target.value)}
               className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"/>
        <span className="text-xs text-gray-500">{rows.length} attività</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full text-sm" data-testid="analytics-table">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
            <tr>
              <th className="px-4 py-3">Attività</th>
              <th className="px-4 py-3">Città</th>
              <th className="px-4 py-3 text-right">Views</th>
              <th className="px-4 py-3 text-right">Details</th>
              <th className="px-4 py-3 text-right">Call</th>
              <th className="px-4 py-3 text-right">Directions</th>
              <th className="px-4 py-3 text-right">Website</th>
              <th className="px-4 py-3 text-right font-bold">Totale</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="text-center py-8 text-gray-500"><Loader2 className="inline animate-spin" size={20}/> Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="8" className="text-center py-8 text-gray-500">Nessun dato per il periodo selezionato</td></tr>
            ) : rows.map(r => (
              <tr key={r.activity_id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-semibold text-gray-900">{r.name || r.activity_id}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{r.city_slug}</td>
                <td className="px-4 py-3 text-right">{r.views}</td>
                <td className="px-4 py-3 text-right">{r.clicks}</td>
                <td className="px-4 py-3 text-right">{r.calls}</td>
                <td className="px-4 py-3 text-right">{r.directions}</td>
                <td className="px-4 py-3 text-right">{r.websites}</td>
                <td className="px-4 py-3 text-right font-bold text-gray-900">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700', indigo: 'bg-indigo-50 text-indigo-700',
    green: 'bg-green-50 text-green-700', amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700'
  };
  return (
    <div className={`${colors[color]} rounded-lg p-4`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-70"><Icon size={14}/> {label}</div>
      <div className="mt-1 text-2xl font-bold" style={{ color: '#0f172a' }}>{value.toLocaleString('it-IT')}</div>
    </div>
  );
}
